import { useState } from 'react'
import { useRouter } from '@tanstack/react-router'
import { useGetTryOnGenerations } from '@/hooks/api/useTryOn'
import { TryOnGridCard } from './TryOnGridCard'
import { TryOnViewModal } from './TryOnViewModal'
import { STANDARD_PRODUCT_GRID_SPACING } from '@/constants/gridBreakpoints'

interface TryOnGridProps {
    archived?: boolean
}

export function TryOnGrid({ archived = false }: TryOnGridProps) {
    const router = useRouter()
    const [selectedGenerationId, setSelectedGenerationId] = useState<
        string | null
    >(null)
    const [isModalOpen, setIsModalOpen] = useState(false)

    const {
        data: generations,
        isLoading,
        error,
    } = useGetTryOnGenerations(archived)

    const handleCardClick = (generationId: string) => {
        setSelectedGenerationId(generationId)
        setIsModalOpen(true)
    }

    const handleCloseModal = () => {
        setIsModalOpen(false)
        setSelectedGenerationId(null)
    }

    const handleViewProduct = (productId: string) => {
        router.navigate({ to: '/products/$id', params: { id: productId } })
    }

    if (error) {
        return (
            <div className="flex flex-col items-center justify-center py-6 md:py-12">
                <p className="text-red-500">
                    Failed to load try-ons. Please try again.
                </p>
            </div>
        )
    }

    if (isLoading) {
        return (
            <section
                className={`grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 ${STANDARD_PRODUCT_GRID_SPACING.container}`}
            >
                {[1, 2, 3, 4, 5, 6].map((i) => (
                    <div
                        key={i}
                        className={STANDARD_PRODUCT_GRID_SPACING.items}
                    >
                        <div className="aspect-3/4 animate-pulse rounded-2xl bg-gray-200" />
                    </div>
                ))}
            </section>
        )
    }

    if (!generations || generations.length === 0) {
        return (
            <div className="flex flex-col items-center justify-center py-6 md:py-12">
                <p className="text-gray-500">
                    {archived
                        ? 'No archived try-ons yet'
                        : 'No try-ons yet. Generate some from product pages!'}
                </p>
            </div>
        )
    }

    return (
        <>
            <section
                className={`grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 ${STANDARD_PRODUCT_GRID_SPACING.container}`}
            >
                {generations.map((generation) => (
                    <div
                        key={generation.id}
                        className={STANDARD_PRODUCT_GRID_SPACING.items}
                    >
                        <TryOnGridCard
                            generation={generation}
                            isArchived={archived}
                            onClick={() => handleCardClick(generation.id)}
                        />
                    </div>
                ))}
            </section>

            {/* Try-On View Modal */}
            {selectedGenerationId && generations && (
                <TryOnViewModal
                    open={isModalOpen}
                    onClose={handleCloseModal}
                    generations={generations}
                    initialGenerationId={selectedGenerationId}
                    onViewProduct={handleViewProduct}
                />
            )}
        </>
    )
}
